import React, { useMemo, useRef } from 'react';
import AgChartsWrapper from '../AgChartsWrapper';
import type { AgChartOptions } from '../AgChartsWrapper';
import type { ThemeMode } from '../../../../../types';
import {
  DEFAULT_COLORS,
  buildAgChartTheme,
  buildCartesianAxes,
  buildMultiAxes,
  computeAxisLayout,
  computeFixedRange,
  buildEndValueTags,
  buildLegendConfig,
  getThemeTokens,
  formatDate,
  SERIES_DEFAULTS,
} from '../../../design';
import type { AxisLabelFormatter, ChartTooltipParams, ChartTooltipResult } from '../../../design';

export interface LineChartSeries {
  yKey: string;
  yName: string;
  color?: string;
  fill?: boolean;
  fillOpacity?: number;
  stacked?: boolean;
  markers?: boolean;
  /** Axis ID — series sharing the same ID share the same Y axis.
   *  Omit for single-axis charts. Use different IDs for multi-axis. */
  axis?: string;
  /** 'step' holds each value until the next point (position: 'end') — for
   *  sparsely-published series like biweekly short interest. */
  interpolation?: 'step' | 'linear' | 'smooth';
  /** Draw through null gaps — needed when a sparse series rides on rows
   *  driven by a denser series (e.g. settlements on daily price rows). */
  connectMissingData?: boolean;
  /** Dashed stroke — for reference/overlay lines (e.g. an analyst's own
   *  estimate against measured series) rather than data. */
  dashed?: boolean;
  tooltipRenderer?: (params: ChartTooltipParams) => ChartTooltipResult;
}

export interface LineChartHoverDatum {
  datum: Record<string, unknown>;
  xKey: string;
  yKey: string;
  xValue: unknown;
  yValue: unknown;
  yName?: string;
}

export interface LineChartProps {
  data: Record<string, unknown>[];
  xKey: string;
  yKey?: string;
  series?: LineChartSeries[];
  colors?: string[];
  title?: string;
  legend?: boolean;
  legendValues?: boolean;
  legendPosition?: 'top' | 'bottom' | 'left' | 'right';
  stacked?: boolean;
  markerThreshold?: number;
  showEndValueTags?: boolean;
  endValueFormatter?: (v: number) => string;
  xLabelFormatter?: AxisLabelFormatter;
  /** Explicit x-axis tick values (must exactly match category values).
   *  Don't combine with a label formatter that index-thins. */
  xTickValues?: Array<string | number>;
  /** Axis titles: x plus per-axis-id y titles (single-axis charts use the
   *  default id 'a'). */
  xTitle?: string;
  yTitles?: Record<string, string>;
  /** DS: dual-axis labels color-coded to their series (per axis ID). */
  axisColors?: Record<string, string>;
  /** Axis IDs that draw a zero baseline crossLine. */
  zeroLineAxes?: string[];
  /** Y-axis label formatters keyed by axis ID (e.g. { b: axisCompact }) */
  yLabelFormatters?: Record<string, AxisLabelFormatter>;
  /** Target tick-interval count per axis ID (higher = more labels; default ~5). */
  yTickCounts?: Record<string, number>;
  /** Secondary axis IDs whose zero aligns with the primary axis's zero. */
  alignZeroAxes?: string[];
  /** Shorthand: single Y-axis label formatter (used when only 1 axis) */
  yLabelFormatter?: AxisLabelFormatter;
  hiddenSeries?: Set<string>;
  theme?: ThemeMode;
  enableCrosshair?: boolean;
  hoverSeriesKey?: string;
  onDatumHover?: (value: LineChartHoverDatum | null) => void;
}

const LineChart: React.FC<LineChartProps> = ({
  data,
  xKey,
  yKey,
  series: seriesProp,
  colors: colorsProp,
  title,
  legend,
  legendValues,
  legendPosition = 'bottom',
  stacked: chartStacked,
  markerThreshold = 50,
  showEndValueTags,
  endValueFormatter,
  xLabelFormatter,
  xTickValues,
  xTitle,
  yTitles,
  axisColors,
  zeroLineAxes,
  yLabelFormatters,
  yLabelFormatter,
  yTickCounts,
  alignZeroAxes,
  hiddenSeries,
  theme: themeMode = 'light',
  enableCrosshair = false,
  hoverSeriesKey,
  onDatumHover,
}) => {
  const lastHoverSignatureRef = useRef<string | null>(null);

  const chartOptions = useMemo(() => {
    const colors = colorsProp || DEFAULT_COLORS;
    const autoMarkers = data.length < markerThreshold;

    const allSeries: LineChartSeries[] = seriesProp
      || (yKey ? [{ yKey, yName: yKey }] : []);

    const colorMap = new Map<string, string>();
    allSeries.forEach((s, i) => {
      colorMap.set(s.yKey, s.color || colors[i % colors.length]);
    });

    const seriesList = hiddenSeries?.size
      ? allSeries.filter((s) => !hiddenSeries.has(s.yKey))
      : allSeries;

    // Compute axis layout from ALL series (before filtering) for stable axes
    const allAxisIds = allSeries.map((s) => s.axis || 'a');
    const uniqueAxisIds = Array.from(new Set(allAxisIds));
    const isMultiAxis = uniqueAxisIds.length > 1;

    // Determine which sides have axes for dynamic padding
    const axisLayout = isMultiAxis ? computeAxisLayout(uniqueAxisIds) : null;
    const hasLeftAxis = isMultiAxis && Array.from(axisLayout!.values()).some((l) => l.position === 'left');
    const hasRightAxis = !isMultiAxis || Array.from(axisLayout!.values()).some((l) => l.position === 'right');
    const agTheme = buildAgChartTheme(colors, themeMode, { hasLeftAxis, hasRightAxis, hasEndValueTags: showEndValueTags });

    // Compute fixed ranges from ALL series (so hiding doesn't rescale)
    const fixedRanges: Record<string, { min: number; max: number }> = {};
    for (const axisId of uniqueAxisIds) {
      const keysOnAxis = allSeries
        .filter((s) => (s.axis || 'a') === axisId)
        .map((s) => s.yKey);
      const hasStackedOnAxis = allSeries
        .some((s) => (s.axis || 'a') === axisId && (s.stacked || (chartStacked && s.fill)));
      const range = computeFixedRange(data, keysOnAxis, hasStackedOnAxis);
      // A zero-baseline axis must actually contain 0 — an area fill anchored
      // at a zero outside the range would render clipped/dishonest.
      if (zeroLineAxes?.includes(axisId)) {
        range.min = Math.min(range.min, 0);
        range.max = Math.max(range.max, 0);
      }
      fixedRanges[axisId] = range;
    }

    // Build axes
    let axes: Record<string, unknown>;
    if (isMultiAxis) {
      const layout = computeAxisLayout(uniqueAxisIds);
      const fmts: Record<string, AxisLabelFormatter> = { ...yLabelFormatters };
      if (yLabelFormatter && uniqueAxisIds.length > 0 && !fmts[uniqueAxisIds[0]]) {
        fmts[uniqueAxisIds[0]] = yLabelFormatter;
      }
      axes = buildMultiAxes({ layout, mode: themeMode, xLabelFormatter, xTickValues, xTitle, yTitles, axisColors, zeroLineAxes, yLabelFormatters: fmts, yTickCounts, alignZeroAxes, fixedRanges });
    } else {
      axes = buildCartesianAxes({
        xLabelFormatter,
        xTickValues,
        xTitle,
        yTitle: yTitles?.[uniqueAxisIds[0]],
        yLabelFormatter: yLabelFormatter || yLabelFormatters?.[uniqueAxisIds[0]],
        yPosition: 'right',
        fixedRange: fixedRanges[uniqueAxisIds[0]],
      });
    }

    if (enableCrosshair) {
      const xAxis = axes.x as Record<string, unknown> | undefined;
      if (xAxis) {
        xAxis.crosshair = {
          enabled: true,
          snap: true,
          stroke: getThemeTokens(themeMode).crosshair.stroke,
          strokeWidth: 1,
          strokeOpacity: 1,
          lineDash: [4, 4],
          label: { enabled: false },
        };
      }
    }

    // End-value tags via crossLines
    if (showEndValueTags) {
      if (isMultiAxis) {
        const axisLayoutMap = computeAxisLayout(uniqueAxisIds);
        axisLayoutMap.forEach((layout, axisId) => {
          const axisSeries = seriesList
            .filter((s) => (s.axis || 'a') === axisId)
            .map((s) => ({ yKey: s.yKey, yName: s.yName, color: colorMap.get(s.yKey) }));
          if (axisSeries.length === 0) return;
          const isAxisStacked = axisSeries.some((as) => {
            const orig = seriesList.find((s) => s.yKey === as.yKey);
            return orig && (orig.stacked || (chartStacked && orig.fill));
          });
          const crossLines = buildEndValueTags(data, axisSeries, colors, endValueFormatter, layout.position, isAxisStacked);
          if (crossLines.length > 0) {
            const axisObj = axes[layout.key] as Record<string, unknown> | undefined;
            // Concat — the axis may already carry a zero-line crossLine.
            if (axisObj) axisObj.crossLines = [...((axisObj.crossLines as unknown[]) ?? []), ...crossLines];
          }
        });
      } else {
        const seriesWithColors = seriesList.map((s) => ({
          yKey: s.yKey, yName: s.yName, color: colorMap.get(s.yKey),
        }));
        const hasStacked = seriesList.some((s) => s.stacked || (chartStacked && s.fill));
        const crossLines = buildEndValueTags(data, seriesWithColors, colors, endValueFormatter, 'right', hasStacked);
        if (crossLines.length > 0) {
          const yAxis = axes.y as Record<string, unknown> | undefined;
          if (yAxis) yAxis.crossLines = [...((yAxis?.crossLines as unknown[]) ?? []), ...crossLines];
        }
      }
    }

    // Build series
    const layout = isMultiAxis ? computeAxisLayout(uniqueAxisIds) : null;

    const agSeries = seriesList.map((s) => {
      const color = colorMap.get(s.yKey)!;
      const showMarkers = s.markers ?? autoMarkers;
      const useArea = s.fill === true;
      const isStacked = s.stacked || (chartStacked && useArea);
      const axisId = s.axis || 'a';
      const axisBinding = layout ? { yKeyAxis: layout.get(axisId)!.key } : {};
      const shouldTrackHover = !!onDatumHover && s.yKey === (hoverSeriesKey || seriesList[0]?.yKey);
      const tooltipRenderer = s.tooltipRenderer || shouldTrackHover
        ? (params: ChartTooltipParams) => {
            if (shouldTrackHover) {
              const datum = params?.datum as Record<string, unknown> | undefined;
              const xValue = datum?.[xKey];
              const yValue = datum?.[s.yKey];
              const signature = `${String(xValue)}::${String(yValue)}`;
              if (signature !== lastHoverSignatureRef.current) {
                lastHoverSignatureRef.current = signature;
                onDatumHover?.({
                  datum: datum || {},
                  xKey,
                  yKey: s.yKey,
                  xValue,
                  yValue,
                  yName: s.yName,
                });
              }
            }

            if (s.tooltipRenderer) {
              return s.tooltipRenderer(params);
            }

            // v13 contract: rows go in `data` (a returned `content` string is
            // silently dropped); in shared mode AG merges every series' rows
            // under one heading and prepends each series' color dot. Format
            // the value with the series' axis formatter so this row matches
            // the rows AG generates for renderer-less series.
            const xValue = params?.datum?.[xKey];
            const yValue = params?.datum?.[s.yKey];
            const fmt = yLabelFormatters?.[s.axis || 'a'] ?? yLabelFormatter;
            const value =
              yValue == null
                ? '--'
                : typeof yValue === 'number' && fmt
                  ? fmt({ value: yValue } as Parameters<AxisLabelFormatter>[0])
                  : String(yValue);
            return {
              heading: xValue == null ? undefined : formatDate(String(xValue)),
              data: [{ label: s.yName, value }],
            };
          }
        : undefined;
      const tooltipOpts = tooltipRenderer ? { tooltip: { renderer: tooltipRenderer } } : {};

      if (useArea) {
        return {
          type: 'area' as const,
          xKey,
          yKey: s.yKey,
          yName: s.yName,
          fill: color,
          stroke: color,
          fillOpacity: s.fillOpacity ?? SERIES_DEFAULTS.area.fillOpacity,
          strokeWidth: SERIES_DEFAULTS.area.strokeWidth,
          marker: { enabled: showMarkers, size: SERIES_DEFAULTS.area.marker.size, fill: color },
          ...(s.interpolation === 'step' ? { interpolation: { type: 'step' as const, position: 'end' as const } } : {}),
          ...(s.interpolation === 'smooth' ? { interpolation: { type: 'smooth' as const } } : {}),
          ...(s.connectMissingData ? { connectMissingData: true } : {}),
          ...tooltipOpts,
          ...(isStacked ? { stacked: true } : {}),
          ...axisBinding,
        };
      }

      return {
        type: 'line' as const,
        xKey,
        yKey: s.yKey,
        yName: s.yName,
        stroke: color,
        strokeWidth: SERIES_DEFAULTS.line.strokeWidth,
        marker: {
          enabled: showMarkers,
          size: SERIES_DEFAULTS.line.marker.size,
          strokeWidth: SERIES_DEFAULTS.line.marker.strokeWidth,
          stroke: SERIES_DEFAULTS.line.marker.stroke,
          fill: color,
        },
        ...(s.interpolation === 'step' ? { interpolation: { type: 'step' as const, position: 'end' as const } } : {}),
        ...(s.interpolation === 'smooth' ? { interpolation: { type: 'smooth' as const } } : {}),
        ...(s.connectMissingData ? { connectMissingData: true } : {}),
        ...(s.dashed ? { lineDash: [6, 4] } : {}),
        ...tooltipOpts,
        ...axisBinding,
      };
    });

    // Legend
    const legendConfig = buildLegendConfig({
      enabled: legend === true || (legend !== false && seriesList.length > 1),
      position: legendPosition,
      legendValues,
      data,
      series: seriesList,
    });

    return {
      theme: agTheme,
      data,
      title: title ? { text: title } : undefined,
      tooltip: { mode: 'shared' as const },
      legend: legendConfig,
      axes,
      series: agSeries,
    };
    // axisColors + zeroLineAxes ARE in this deps array; the CRA build's eslint
    // pipeline false-positives on them (direct `npx eslint` passes clean).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, xKey, yKey, seriesProp, colorsProp, title, legend, legendValues, legendPosition, chartStacked, markerThreshold, showEndValueTags, endValueFormatter, xLabelFormatter, xTickValues, xTitle, yTitles, axisColors, zeroLineAxes, yLabelFormatter, yLabelFormatters, yTickCounts, alignZeroAxes, hiddenSeries, themeMode, enableCrosshair, onDatumHover, hoverSeriesKey]);

  return (
    <div
      className="h-full"
      onMouseLeave={() => {
        lastHoverSignatureRef.current = null;
        onDatumHover?.(null);
      }}
    >
      <AgChartsWrapper options={chartOptions as unknown as AgChartOptions} />
    </div>
  );
};

export default LineChart;
